import type {
  ActionListFullValue,
  ActionListValue,
} from '@latitude/action-list';
import { Select } from '@latitude/select';
import { FormComponentWrapper } from 'components/FormComponentWrapper';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import {
  type FormSelectProps,
  type SyncNarrowedProps,
  isAsyncSelectProps,
} from './types';

export const FormSelect = <
  isMulti extends boolean = false,
  isNullable extends boolean = false,
  ExtraData = never,
  T extends FieldValues = FieldValues,
>(
  props: FormSelectProps<isMulti, isNullable, ExtraData, T>,
) => {
  const {
    name,
    control,
    description,
    label,
    wrapperClassname,
    message,
    disabled,
    rules,
    optional,
    onChange: passedOnChange,
    rawProps: passedRawProps = {},
    ...selectProps
  } = props;

  return (
    <Controller
      name={name}
      {...(control
        ? { control }
        : {
            // if undefined, Controller reads from FormProvider
          })}
      rules={rules}
      render={({
        field: { onBlur, onChange, value },
        fieldState: { error },
      }) => {
        function handleChange(
          val: ActionListValue<isMulti, isNullable>,
          fullValue: ActionListFullValue<isMulti, isNullable, ExtraData>,
        ) {
          if (passedOnChange) {
            passedOnChange(val, fullValue, onChange);
          } else {
            onChange(val);
          }

          if (error) {
            onBlur();
          }
        }

        const sharedProps = {
          disabled,
          value,
          error: !!error as boolean,
          onBlur,
          rawProps: passedRawProps,
        };

        // Type guard narrows selectProps — compiler verifies the union
        // member instead of trusting an `as` cast.
        const propsWithOnChange = { onChange: handleChange, ...selectProps };

        return (
          <FormComponentWrapper
            name={name}
            description={description}
            label={label}
            error={error}
            className={wrapperClassname}
            message={message}
            disabled={disabled}
            optional={optional}
          >
            {isAsyncSelectProps<isNullable, ExtraData>(propsWithOnChange) ? (
              <Select {...sharedProps} {...propsWithOnChange} />
            ) : (
              <Select
                {...sharedProps}
                {...(propsWithOnChange as SyncNarrowedProps<
                  isMulti,
                  isNullable,
                  ExtraData
                >)}
              />
            )}
          </FormComponentWrapper>
        );
      }}
    />
  );
};
